import { useCallback, useEffect, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { useToast } from '../ui/toast.jsx';
import { LevelBadge, HttpStatusBadge, MethodBadge } from '../ui/badges.jsx';
import { EmptyState } from '../ui/empty-state.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';

const REFRESH_HINT = 'Logs are read from disk. Use Refresh after generating new traffic.';
const PAGE_SIZE = 50;

export function LogsPage({ currentUser }) {
  const [tab, setTab] = useState('app');
  const [files, setFiles] = useState([]);

  const refreshFiles = useCallback(() => {
    api.logs
      .files()
      .then((r) => setFiles(r.files || []))
      .catch(() => setFiles([]));
  }, []);

  useEffect(() => {
    refreshFiles();
  }, [refreshFiles]);

  return (
    <PageShell
      icon="fa-clipboard-list"
      title="Logs"
      subtitle="HTTP and application logs from the local file system"
    >
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {files.map((f) => (
          <FileCard key={f.name} file={f} viewerTimezone={currentUser?.timezone} />
        ))}
      </div>

      <div className="mb-4 flex items-center justify-between gap-3">
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { id: 'app',  label: 'Application', icon: 'fa-rectangle-terminal' },
            { id: 'http', label: 'HTTP',        icon: 'fa-globe' },
          ]}
        />
        <p className="hidden text-xs text-mist sm:block">{REFRESH_HINT}</p>
      </div>

      {tab === 'app' && <AppLogPane onChanged={refreshFiles} viewerTimezone={currentUser?.timezone} />}
      {tab === 'http' && <HttpLogPane onChanged={refreshFiles} viewerTimezone={currentUser?.timezone} />}
    </PageShell>
  );
}

// -----------------------------------------------------------------------------
//  Tabs
// -----------------------------------------------------------------------------
function Tabs({ value, onChange, items }) {
  return (
    <div role="tablist" className="flex w-fit items-center gap-1 rounded-2xl border border-line bg-white p-1 shadow-card">
      {items.map((item) => {
        const active = value === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={
              active
                ? 'flex items-center gap-2 rounded-xl bg-accent-soft px-4 py-2 text-sm font-semibold text-accent'
                : 'flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-mist hover:text-ink'
            }
          >
            <i className={`fa-solid ${item.icon} text-[13px]`} aria-hidden="true" />
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function FileCard({ file, viewerTimezone }) {
  const sizeKb = Math.round(file.size / 1024);
  return (
    <div className="card flex items-center justify-between px-5 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-canvas text-mist">
          <i className={`fa-solid ${file.name === 'http' ? 'fa-globe' : 'fa-rectangle-terminal'}`} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-ink">{file.file}</div>
          <div className="truncate text-xs text-mist">
            {sizeKb} KB · last modified{' '}
            {file.modifiedAt ? formatUtcInTimezone(file.modifiedAt, viewerTimezone) : '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Application log
// -----------------------------------------------------------------------------
const APP_LEVELS = ['all', 'error', 'warn', 'info', 'debug'];

function AppLogPane({ onChanged, viewerTimezone }) {
  const toast = useToast();
  const [level, setLevel] = useState('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  // Filter changes reset to page 0.
  useEffect(() => { setPage(0); }, [level, q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.logs.app({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        level: level === 'all' ? '' : level,
        q,
      });
      setData(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, [level, q, page]);

  useEffect(() => {
    load();
  }, [load]);

  async function clearFile() {
    try {
      await api.logs.clear('app');
      toast.success('Application log cleared.');
      onChanged?.();
      setPage(0);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Failed to clear log.');
    } finally {
      setConfirmClear(false);
    }
  }

  const total = data?.total ?? 0;
  const totalPages = data ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-line px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-1 rounded-2xl border border-line bg-white p-1">
            {APP_LEVELS.map((lv) => (
              <button
                key={lv}
                type="button"
                onClick={() => setLevel(lv)}
                className={
                  level === lv
                    ? 'rounded-xl bg-accent-soft px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent'
                    : 'rounded-xl px-3 py-1 text-xs font-semibold uppercase tracking-wider text-mist hover:text-ink'
                }
              >
                {lv}
              </button>
            ))}
          </div>
          <div className="relative w-full max-w-xs">
            <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-mist" aria-hidden="true" />
            <input
              type="search"
              className="field pl-10"
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} disabled={loading} className="btn-secondary text-xs">
            <i className={`fa-solid fa-arrows-rotate ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </button>
          <button type="button" onClick={() => setConfirmClear(true)} className="btn-secondary text-xs text-rose-700 hover:bg-rose-soft">
            <i className="fa-solid fa-trash" aria-hidden="true" />
            Clear
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="border-b border-rose-200 bg-rose-soft px-5 py-3 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      <PaneStats data={data} loading={loading} page={page} totalPages={totalPages} />

      {data && data.items.length === 0 ? (
        <EmptyState icon="fa-folder-open" title="No matching log entries" description="Try changing the filter or clearing the search field." />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
                  <th className="w-48 px-5 py-2.5">Time</th>
                  <th className="w-24 px-5 py-2.5">Level</th>
                  <th className="px-5 py-2.5">Message</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((entry, idx) => (
                  <AppRow key={page * PAGE_SIZE + idx} entry={entry} viewerTimezone={viewerTimezone} />
                ))}
              </tbody>
            </table>
          </div>

          {data && total > PAGE_SIZE && (
            <Pager page={page} totalPages={totalPages} onChange={setPage} />
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={clearFile}
        title="Clear application log?"
        message="This permanently truncates app.log on disk. Audit events are kept in the audit database. Continue?"
        confirmLabel="Clear app.log"
        destructive
      />
    </div>
  );
}

function AppRow({ entry, viewerTimezone }) {
  const [open, setOpen] = useState(false);
  const hasMeta = entry.meta && Object.keys(entry.meta).length > 0;
  return (
    <>
      <tr
        className={`border-b border-line last:border-b-0 ${hasMeta ? 'cursor-pointer hover:bg-canvas/40' : ''}`}
        onClick={hasMeta ? () => setOpen((v) => !v) : undefined}
      >
        <td className="whitespace-nowrap px-5 py-2.5 align-top text-xs text-mist">
          {entry.ts ? formatUtcInTimezone(entry.ts, viewerTimezone) : '—'}
        </td>
        <td className="px-5 py-2.5 align-top">
          <LevelBadge level={entry.level} />
        </td>
        <td className="px-5 py-2.5 align-top">
          <div className="flex items-start justify-between gap-3">
            <span className={entry.level === 'error' ? 'text-rose-700' : 'text-ink'}>{entry.msg}</span>
            {hasMeta && (
              <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'} mt-1 text-[10px] text-mist`} aria-hidden="true" />
            )}
          </div>
        </td>
      </tr>
      {open && hasMeta && (
        <tr className="bg-canvas/40">
          <td colSpan={3} className="px-5 py-3">
            <pre className="overflow-x-auto rounded-xl bg-ink/95 p-3 text-[11px] text-white/90 scrollbar-thin">
              {JSON.stringify(entry.meta, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
//  HTTP log
// -----------------------------------------------------------------------------
function HttpLogPane({ onChanged, viewerTimezone }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => { setPage(0); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.logs.http({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, q });
      setData(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => {
    load();
  }, [load]);

  async function clearFile() {
    try {
      await api.logs.clear('http');
      toast.success('HTTP log cleared.');
      onChanged?.();
      setPage(0);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Failed to clear log.');
    } finally {
      setConfirmClear(false);
    }
  }

  const total = data?.total ?? 0;
  const totalPages = data ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-line px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-xs">
          <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-mist" aria-hidden="true" />
          <input
            type="search"
            className="field pl-10"
            placeholder="Search by URL, IP, status…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} disabled={loading} className="btn-secondary text-xs">
            <i className={`fa-solid fa-arrows-rotate ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </button>
          <button type="button" onClick={() => setConfirmClear(true)} className="btn-secondary text-xs text-rose-700 hover:bg-rose-soft">
            <i className="fa-solid fa-trash" aria-hidden="true" />
            Clear
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="border-b border-rose-200 bg-rose-soft px-5 py-3 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      <PaneStats data={data} loading={loading} page={page} totalPages={totalPages} />

      {data && data.items.length === 0 ? (
        <EmptyState icon="fa-folder-open" title="No HTTP requests yet" description="Generate traffic and refresh." />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
                  <th className="w-44 px-5 py-2.5">Time</th>
                  <th className="w-32 px-5 py-2.5">IP</th>
                  <th className="w-20 px-5 py-2.5">Method</th>
                  <th className="w-16 px-5 py-2.5">Status</th>
                  <th className="px-5 py-2.5">URL</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((entry, idx) => (
                  <HttpRow key={page * PAGE_SIZE + idx} entry={entry} viewerTimezone={viewerTimezone} />
                ))}
              </tbody>
            </table>
          </div>

          {data && total > PAGE_SIZE && (
            <Pager page={page} totalPages={totalPages} onChange={setPage} />
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={clearFile}
        title="Clear HTTP log?"
        message="This permanently truncates http.log on disk. Continue?"
        confirmLabel="Clear http.log"
        destructive
      />
    </div>
  );
}

function HttpRow({ entry, viewerTimezone }) {
  if (!entry.parsed) {
    return (
      <tr className="border-b border-line last:border-b-0">
        <td colSpan={5} className="px-5 py-2.5 text-xs text-mist font-mono">
          {entry.raw}
        </td>
      </tr>
    );
  }
  const ts = clfToIso(entry.dateClf);
  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-canvas/40">
      <td className="whitespace-nowrap px-5 py-2.5 text-xs text-mist">
        {ts ? formatUtcInTimezone(ts, viewerTimezone) : entry.dateClf}
      </td>
      <td className="px-5 py-2.5 font-mono text-xs text-ink">{entry.ip}</td>
      <td className="px-5 py-2.5"><MethodBadge method={entry.method} /></td>
      <td className="px-5 py-2.5"><HttpStatusBadge status={entry.status} /></td>
      <td className="px-5 py-2.5 font-mono text-xs text-ink">
        <span className="block truncate" title={entry.url}>{entry.url}</span>
      </td>
    </tr>
  );
}

function clfToIso(clf) {
  // 07/May/2026:00:32:15 +0000  →  2026-05-07T00:32:15Z
  if (!clf) return null;
  const m = clf.match(/^(\d{2})\/(\w{3})\/(\d{4}):(\d{2}):(\d{2}):(\d{2}) ([+-]\d{4})$/);
  if (!m) return null;
  const [, dd, mon, yyyy, hh, mi, ss, off] = m;
  const months = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06', Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };
  const mm = months[mon];
  if (!mm) return null;
  // The file is written with the server's offset (CLF style); always keep as ISO with offset normalised to Z by re-computing.
  const local = new Date(`${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}${off.slice(0, 3)}:${off.slice(3)}`);
  if (Number.isNaN(local.getTime())) return null;
  return local.toISOString();
}

function PaneStats({ data, loading, page, totalPages }) {
  if (loading && !data) {
    return (
      <div className="px-5 py-12 text-center text-mist">
        <span className="loader-spinner mx-auto mb-2 text-accent" aria-hidden="true" />
        <div className="text-sm">Reading log…</div>
      </div>
    );
  }
  if (!data) return null;
  const total = data.total ?? data.available ?? 0;
  const start = total === 0 ? 0 : page * 50 + 1;
  const end = Math.min((page + 1) * 50, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-canvas/40 px-5 py-2 text-[11px] text-mist">
      <span>
        Showing <strong className="text-ink">{start}</strong>–<strong className="text-ink">{end}</strong>{' '}
        of <strong className="text-ink">{total}</strong> entries
        {data.truncated && ' · file is large; tail only'}
      </span>
      <span>
        Page {page + 1} of {totalPages} · File size: {Math.round((data.fileSize || 0) / 1024)} KB
      </span>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Pagination — same shape as Audit page
// -----------------------------------------------------------------------------
function Pager({ page, totalPages, onChange }) {
  const prev = () => onChange(Math.max(0, page - 1));
  const next = () => onChange(Math.min(totalPages - 1, page + 1));
  return (
    <div className="flex items-center justify-end gap-2 border-t border-line bg-canvas/40 px-5 py-3">
      <button type="button" onClick={() => onChange(0)} disabled={page === 0} className="btn-secondary text-xs">
        <i className="fa-solid fa-angles-left" aria-hidden="true" /> First
      </button>
      <button type="button" onClick={prev} disabled={page === 0} className="btn-secondary text-xs">
        <i className="fa-solid fa-angle-left" aria-hidden="true" /> Prev
      </button>
      <span className="px-2 text-xs text-mist">
        Page <strong className="text-ink">{page + 1}</strong> / {totalPages}
      </span>
      <button type="button" onClick={next} disabled={page >= totalPages - 1} className="btn-secondary text-xs">
        Next <i className="fa-solid fa-angle-right" aria-hidden="true" />
      </button>
      <button type="button" onClick={() => onChange(totalPages - 1)} disabled={page >= totalPages - 1} className="btn-secondary text-xs">
        Last <i className="fa-solid fa-angles-right" aria-hidden="true" />
      </button>
    </div>
  );
}
